import { Link, useLocation } from '@remix-run/react';
import { Button, buttonVariants } from "~/components/ui/button";
import { cn } from "~/lib/utils";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuSeparator } from "~/components/ui/dropdown-menu";
import { MenuIcon, LogOutIcon, HomeIcon, DropletIcon, SettingsIcon, MessageCircleIcon } from "lucide-react";
import { UserData } from '~/dataType';
import { APP } from '~/lib/constants';
import { useState } from 'react';
import { SignOutConfirmModal } from '~/components/SignOutConfirmModal';

export const Navbar = ({ user }: { user?: UserData | null }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const location = useLocation();
  const isLatestActive = location.pathname === '/latest';
  const isDewsActive = location.pathname === '/dews' || location.pathname.startsWith('/dews/');

  return (
    <>
      <header
        className="bg-white/100 backdrop-blur-md sticky top-0 flex h-16 items-center gap-4 px-4 md:px-6 z-[100]"
        // style={{ boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.09)' }}
      >
        <nav className="grid grid-cols-3 items-center text-xs md:text-sm font-medium w-full">
          {/* 左カラム: ロゴ */}
          <div>
            <Link
              to={user ? `/latest` : `/`}
              className="font-semibold transition-colors hover:text-foreground flex items-center"
            >
              <img src="/dewdrop_icon_2.png" alt="Dewdrop" className="w-7 h-7" />
              <img src="/dewdrop_name.png" alt="Dewdrop" className="h-6 ml-1 mt-0.5" />
              <span className="ml-2 mt-0.5 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full font-medium">
                BETA
              </span>
            </Link>
          </div>

          {/* 中央カラム: デスクトップナビゲーション */}
          {user && (
            <div className="justify-center flex">
              <div className='hidden md:flex gap-8'>
                <Link
                  to={`/latest`}
                  className={cn(
                    buttonVariants({ variant: "ghost" }),
                    "font-semibold transition-colors",
                    isLatestActive ? "bg-accent text-accent-foreground" : ""
                  )}
                >
                  Latest
                </Link>
                <Link
                  to={`/dews`}
                  className={cn(
                    buttonVariants({ variant: "ghost" }),
                    "font-semibold transition-colors",
                    isDewsActive ? "bg-accent text-accent-foreground" : ""
                  )}
                >
                  Dews
                </Link>
              </div>
            </div>
          )}

          {/* 右カラム: ハンバーガーメニュー */}
          {user && (
            <div className="justify-self-end">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant={'ghost'} className={'focus-visible:ring-0 p-2'}>
                    <MenuIcon className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-64 z-[100]" align="end">
                  {/* ユーザー情報エリア */}
                  <div className="px-3 py-3 border-b">
                    <div className="font-medium text-sm">{user.fullName || user.name}</div>
                    <div className="text-xs text-slate-500">{user.email}</div>
                  </div>

                  <DropdownMenuGroup>
                    <DropdownMenuItem asChild>
                      <Link to={`/latest`} className="flex items-center">
                        <HomeIcon className="w-4 h-4 mr-3" />
                        Latest
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to={`/dews`} className="flex items-center">
                        <DropletIcon className="w-4 h-4 mr-3" />
                        Dews
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to={`/settings`} className="flex items-center">
                        <SettingsIcon className="w-4 h-4 mr-3" />
                        Settings
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to={APP.CONTACT_URL} className="flex items-center" target="_blank" rel="noreferrer">
                        <MessageCircleIcon className="w-4 h-4 mr-3" />
                        Contact
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    <DropdownMenuItem onSelect={() => setIsModalOpen(true)}>
                      <div className='flex items-center w-full cursor-pointer'>
                        <LogOutIcon className="w-4 h-4 mr-3" />
                        Logout
                      </div>
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
          {/* 中央カラム: ログインしていない場合は空 */}
          {!user && <div></div>}

          {/* 右カラム: Sign In ボタン */}
          {!user && (
            <div className="justify-self-end">
              <Link
                to="/signin"
                className="bg-black text-white px-4 py-2 rounded-md inline-block hover:bg-gray-500 transition-colors font-semibold"
              >
                Sign In
              </Link>
            </div>
          )}
        </nav>
      </header>

      <SignOutConfirmModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />

    </>
  );
};
